import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, createRoutesFromElements, matchRoutes, useParams } from "react-router-dom";
import RouteAnnouncer from "./a11y/RouteAnnouncer";
import ScrollToTop from "./a11y/ScrollToTop";
import SessionWarning from "./a11y/SessionWarning";
import RouteFallback, { RouteFailed } from "./component/RouteFallback";

// Every page is its own chunk: a visit downloads the shared shell (foundation, navbar, footer, dialogs) and the
// page it opens, not the other 30 pages and their stylesheets. Pages whose stylesheets build on one another
// (Trade Ideas and a trade idea, the account pages, checkout and KYC) share one chunk (the same webpackChunkName),
// so a stylesheet another one overrides is never loaded a second time after it.

// page(): a lazy page that renders at once (no fallback) when its code is already in, e.g. after preloadRoute.
const page = (load) => {
  let Loaded = null;
  const loadOnce = () =>
    load().then((m) => {
      Loaded = m.default;
      return m;
    });
  const Lazy = lazy(loadOnce);
  const Page = (props) => (Loaded ? <Loaded {...props} /> : <Lazy {...props} />);
  Page.preload = loadOnce;
  return Page;
};
const HomePage = page(() => import(/* webpackChunkName: "home" */ "./component/HomePage"));
const Portfolios = page(() => import(/* webpackChunkName: "portfolios" */ "./component/Portfolios"));
const PortfolioDetails = page(() => import(/* webpackChunkName: "portfolio-details" */ "./component/PortfolioDetails"));
const Research = page(() => import(/* webpackChunkName: "research" */ "./component/research"));
const ResearchAnswer = page(() => import(/* webpackChunkName: "research-answer" */ "./component/ResearchAnswer"));
const FactorSchool = page(() => import(/* webpackChunkName: "factor-school" */ "./component/FactorSchool"));
const MarketPulse = page(() => import(/* webpackChunkName: "market-pulse" */ "./component/MarketPulse"));
const SwingIdeas = page(() => import(/* webpackChunkName: "trade-ideas" */ "./component/SwingIdeas"));
const SwingDetailView = page(() => import(/* webpackChunkName: "trade-ideas" */ "./component/SwingDetailView"));
const Pricing = page(() => import(/* webpackChunkName: "pricing" */ "./component/Pricing"));
const Profile = page(() => import(/* webpackChunkName: "account" */ "./component/Profile"));
const EditProfile = page(() => import(/* webpackChunkName: "account" */ "./component/EditProfile"));
const YourSubscriptions = page(() => import(/* webpackChunkName: "account" */ "./component/YourSubscriptions"));
const EsignSuccessPage = page(() => import(/* webpackChunkName: "account" */ "./component/EsignSuccessPage"));
const SessionTimeout = page(() => import(/* webpackChunkName: "account" */ "./component/SessionTimeout"));
const CmsPage = page(() => import(/* webpackChunkName: "cms" */ "./component/CmsPage"));
const DASignal = page(() => import(/* webpackChunkName: "da-signal" */ "./component/DASignal"));
const Checkout = page(() => import(/* webpackChunkName: "checkout" */ "./component/Checkout"));
const KycVerificationPage = page(() => import(/* webpackChunkName: "checkout" */ "./component/KycVerificationPage"));
const BacktesterPage = page(() => import(/* webpackChunkName: "backtester" */ "./backtester/BacktesterPage"));
const BacktesterNewPage = page(() => import(/* webpackChunkName: "backtester-new" */ "./backtester_new/BacktesterPage"));
const Tools = page(() => import(/* webpackChunkName: "tools" */ "./component/Tools"));
const Glossary = page(() => import(/* webpackChunkName: "glossary" */ "./component/Glossary"));
const BlogsAndNews = page(() => import(/* webpackChunkName: "blogs" */ "./component/BlogsAndNews"));
const AccessibilityStatement = page(() => import(/* webpackChunkName: "accessibility" */ "./component/AccessibilityStatement"));
const BossAIPage = page(() => import(/* webpackChunkName: "bossai" */ "./bossai/BossAIPage"));
// import NewsLetterAndBlogs from "./component/NewsLetterAndBlogs";
// import NewsLetterAndBlogsTemplate from "./component/NewsletterAndBlogsTemplate";

// Old backtester details links open the same test in the new backtester.
const BacktestDetailsRedirect = () => {
  const { strategyCode } = useParams();
  return <Navigate to={`/backtester/results?test=${encodeURIComponent(strategyCode || "")}`} replace />;
};

// Every address and the page it opens. Rendered by <Routes> below; preloadRoute reads it too.
const routes = (
  <>
    <Route path="/" element={<HomePage />} />
    <Route path="/session-timeout" element={<SessionTimeout />} />
    <Route path="/portfolios" element={<Portfolios />} />
    <Route path="/backtester" element={<BacktesterPage view="builder" />} />
    <Route path="/backtester/results" element={<BacktesterPage view="results" />} />
    <Route path="/backtester/tests" element={<BacktesterPage view="tests" />} />
    <Route path="/backtester/library" element={<Navigate to="/backtester/tests" replace />} />
    <Route path="/backtester/compare" element={<Navigate to="/backtester/tests" replace />} />
    <Route path="/StrategyBuilderPage" element={<Navigate to="/backtester" replace />} />
    <Route path="/daSignal" element={<DASignal />} />
    <Route path="/portfolioDetails/:type" element={<PortfolioDetails />} />
    <Route path="/backtesterDetails/:strategyCode" element={<BacktestDetailsRedirect />} />
    {/* The new backtester (templates, NAV, live search), at its own address beside /backtester. */}
    <Route path="/backtester-new" element={<BacktesterNewPage view="start" />} />
    <Route path="/backtester-new/build" element={<BacktesterNewPage view="builder" />} />
    <Route path="/backtester-new/results" element={<BacktesterNewPage view="results" />} />
    <Route path="/backtester-new/tests" element={<BacktesterNewPage view="tests" />} />
    <Route path="/research" element={<Research />} />
    <Route path="/research/:slug" element={<ResearchAnswer />} />
    <Route path="/factorSchool" element={<FactorSchool />} />
    {/* The old static factor pages (public/factorSchool/*.html, framed at /factorInfo/:type) showed backtest win
        rates to signed-out visitors and figures that disagreed with each other; none is linked and none ships
        until its figures are reviewed. Their addresses open Factor School. */}
    <Route path="/factorInfo/*" element={<Navigate to="/factorSchool" replace />} />
    <Route path="/factorSchool/*" element={<Navigate to="/factorSchool" replace />} />
    <Route path="/marketPulse" element={<MarketPulse />} />
    <Route path="/tradeIdeas" element={<SwingIdeas />} />
    <Route path="/tradeDetailView/:type" element={<SwingDetailView />} />
    {/* Storyteller showed typed-in returns, drawdowns and a portfolio "match" to everyone; the portfolios page
        shows performance through the viewer gates instead. */}
    <Route path="/Storyteller" element={<Navigate to="/portfolios" replace />} />
    <Route path="/Pricing" element={<Pricing />} />
    {/* LoginPaid is a dialog the navbar opens; as a page it rendered nothing. */}
    <Route path="/loginPaid" element={<Navigate to="/" replace />} />
    <Route path="/profile" element={<Profile />} />
    <Route path="/edit-profile" element={<EditProfile />} />
    <Route path="/terms-and-conditions" element={<CmsPage type="tnc" />} />
    <Route path="/privacy-policy" element={<CmsPage type="privacy" />} />
    <Route path="/escalation_matrix" element={<CmsPage type="escalation_matrix" />} />
    <Route path="/about" element={<CmsPage type="about" />} />
    <Route path="/contact" element={<CmsPage type="contact" />} />
    <Route path="/faq" element={<CmsPage type="faq" />} />
    <Route path="/shipping" element={<CmsPage type="shipping" />} />
    <Route path="/cancellationrefunds" element={<CmsPage type="cancellationrefunds" />} />
    <Route path="/investor_charter" element={<CmsPage type="investor_charter" />} />
    <Route path="/grievance_redressal" element={<CmsPage type="grievance_redressal" />} />
    <Route path="/statutory_details" element={<CmsPage type="statutory_details" />} />
    <Route path="/investor_monthly_complaints" element={<CmsPage type="investor_monthly_complaints" />} />
    <Route path="/accessibility" element={<AccessibilityStatement />} />
    <Route path="/yourSubscriptions" element={<YourSubscriptions />} />
    <Route path="/checkout" element={<Checkout />} />
    <Route path="/kycVerification" element={<KycVerificationPage />} />
    {/* Credits are bought from the Pricing page now; the old address went blank. */}
    <Route path="/creditsPayment" element={<Navigate to="/Pricing" replace />} />
    <Route path="/tools" element={<Tools />} />
    <Route path="/glossary" element={<Glossary />} />
    <Route path="/esign-success" element={<EsignSuccessPage />} />
    {/* <Route path="/blogs" element={<NewsLetterAndBlogs />} /> */}
    <Route path="/blogs" element={<BlogsAndNews />} />
    <Route path="/bossai" element={<BossAIPage />} />
    {/* <Route path="/blogs/:slug" element={<NewsLetterAndBlogsTemplate />} /> */}
  </>
);
const ROUTES = createRoutesFromElements(routes);

// Download the code of the page an address opens (src/index.js: before a prerendered page is first rendered).
export const preloadRoute = (pathname) => {
  const hit = matchRoutes(ROUTES, pathname);
  const element = hit && hit[hit.length - 1].route.element;
  const preload = element && element.type && element.type.preload;
  return preload ? preload() : Promise.resolve();
};

function App() {
  return (
    <BrowserRouter>
      <RouteAnnouncer />
      <ScrollToTop />
      <SessionWarning />
      <RouteFailed>
        <Suspense fallback={<RouteFallback />}>
          <Routes>{routes}</Routes>
        </Suspense>
      </RouteFailed>
    </BrowserRouter>
  );
}

export default App;
